import { TableFilters } from '@components/TableFilters'
import { useTableUrlSync } from '@hooks/useTableUrlSync'
import { GleapTicket, GleapTicketStatus } from '@models/GleapTicket'
import { fetchAllGleapTickets } from '@services/GleapTicketService'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { SBTable } from '@supplement-bacon/sb-table-filters'
import { numberFormat } from '@utils/currencyFormatter'
import { Alert, Segmented, Tag, Typography, type TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

const { Text } = Typography

type StatusFilter = 'all' | GleapTicketStatus

interface Props {
  title: string
}

const filterOptions: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Closed', value: 'closed' },
]

const columns: TableColumnsType<GleapTicket> = [
  {
    title: 'Subject',
    fixed: 'left',
    render: (record: GleapTicket) =>
      record.title?.trim() ? (
        <Text>{record.title}</Text>
      ) : (
        // Gleap leaves the title empty on conversations it never titled.
        <Text type="secondary">No subject</Text>
      ),
  },
  {
    title: 'Reported by',
    width: 160,
    render: (record: GleapTicket) => <Text>{record.reported_by.name}</Text>,
  },
  {
    title: 'Status',
    width: 100,
    render: (record: GleapTicket) => (
      <Tag color={record.status === 'closed' ? 'green' : 'blue'}>
        {record.status === 'closed' ? 'Closed' : 'Open'}
      </Tag>
    ),
  },
  {
    title: 'Date',
    width: 200,
    render: (record: GleapTicket) => (
      <Text>{dayjs(record.date).format('DD/MM/YYYY HH:mm')}</Text>
    ),
  },
  {
    // Plain copyable id rather than a link: the Gleap dashboard needs an agent
    // seat, so a BO user clicking through would only hit a login wall. Support
    // pastes the id into Gleap themselves.
    title: 'Gleap ID',
    width: 240,
    render: (record: GleapTicket) => (
      <Text copyable={{ text: record.gleap_id }} type="secondary">
        {record.gleap_id}
      </Text>
    ),
  },
]

const GleapTicketList = ({ title }: Props) => {
  const [tickets, setTickets] = useState<GleapTicket[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)

  // URL is the source of truth for the status filter and the current page,
  // via f_/s_/page query params (same as the Redux-backed list pages). The
  // data itself is loaded once, so onParamsChange has nothing to refetch.
  const { handleTableChange, handleFiltersChange, params } =
    useTableUrlSync<GleapTicket>({ onParamsChange: () => {} })

  const selectedStatus = (params.filters.status?.[0] ?? 'all') as StatusFilter

  useEffect(() => {
    let active = true

    fetchAllGleapTickets()
      .then((all) => {
        if (active) setTickets(all)
      })
      .catch(() => {
        if (active) setIsError(true)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  const visibleTickets =
    selectedStatus === 'all'
      ? tickets
      : tickets.filter((ticket) => ticket.status === selectedStatus)

  return (
    <>
      <PageTitle
        level={5}
        title={title}
        style={{ marginBottom: 16 }}
        badge={{
          count: numberFormat(visibleTickets.length),
          overflowCount: 999999,
        }}
      />
      {isError ? (
        <Alert
          type="error"
          showIcon
          title="Unable to load tickets"
          description="An error occurred while fetching tickets. Please try again later."
        />
      ) : (
        <>
          <TableFilters
            initialValues={{ status: selectedStatus }}
            left={[
              {
                name: 'status',
                type: 'filter',
                label: '',
                initialValue: 'all',
                input: <Segmented options={filterOptions} />,
              },
            ]}
            onValuesChange={(filters: any, search: any) => {
              if (filters.status === 'all') delete filters.status
              handleFiltersChange(filters, search)
            }}
          />
          <SBTable
            rowKey="id"
            columns={columns}
            dataSource={visibleTickets}
            loading={isLoading}
            onChange={handleTableChange}
            pagination={{
              current: params.page,
              pageSize: 20,
              showSizeChanger: false,
            }}
            scroll={{ x: 'max-content' }}
            empty={{ description: 'No tickets' }}
          />
        </>
      )}
    </>
  )
}

export default GleapTicketList
